/** ConnectedFootingPage — strap footing system. */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useConnectedStore } from '@/features/zapatas_connected/store';
import { useConnectedAnalysis } from '@/features/zapatas_connected/useAnalysis';
import { useExportConnected } from '@/features/zapatas_connected/useExport';
import { Connected3DView } from '@/features/zapatas_connected/Connected3DView';
import { RhoBar } from '@/components/ui/RhoBar';
import { DesignToolbar } from '@/features/designs/DesignToolbar';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import type { ConnectedAnalyzeRequest, RebarDiam } from '@/features/zapatas_connected/types';

const REBAR_DIAMS: RebarDiam[] = ['3/8', '1/2', '5/8', '3/4', '1', '1 3/8'];

export function ConnectedFootingPage({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const request = useConnectedStore((s) => s.request);
  const setSection = useConnectedStore((s) => s.setSection);
  const setMaterials = useConnectedStore((s) => s.setMaterials);
  const setSoil = useConnectedStore((s) => s.setSoil);
  const setLoads = useConnectedStore((s) => s.setLoads);
  const setReinforcement = useConnectedStore((s) => s.setReinforcement);
  const loadRequest = useConnectedStore((s) => s.loadRequest);

  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data && designQuery.data.subtype === 'connected') {
      loadRequest(designQuery.data.payload as unknown as ConnectedAnalyzeRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const [saveOpen, setSaveOpen] = useState(false);
  const { data, isFetching, isError, error } = useConnectedAnalysis(request);
  const exporter = useExportConnected();
  const [view, setView] = useState<'2D' | '3D'>('2D');

  const matchingDesign =
    designQuery.data && designQuery.data.subtype === 'connected' ? designQuery.data : null;

  return (
    <div className="flex h-full flex-col">
      {!hideHeader && (
        <header className="flex h-14 shrink-0 items-center gap-6 border-b px-6"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}>
          <Link to="/app/footings" className="flex items-center gap-1 text-[11px]"
            style={{ color: 'var(--text-muted)' }}>
            <ArrowLeft size={14} /><span>Hub zapatas</span>
          </Link>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Software E.060 + E.050
            </div>
            <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
              Diseño de zapata conectada (medianera + interior)
            </div>
          </div>
        </header>
      )}

      <DesignToolbar
        status={isError ? 'error' : isFetching ? 'busy' : 'ready'}
        statusLabel={isError ? `Error: ${String(error)}` : undefined}
        onExportPdf={() => exporter.trigger('pdf', request)}
        onExportXlsx={() => exporter.trigger('xlsx', request)}
        busyExport={exporter.busy}
        onSave={() => setSaveOpen(true)}
        isUpdate={!!matchingDesign}
      />
      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="zapatas"
        subtype="connected"
        payload={request as unknown as Record<string, unknown>}
        existing={matchingDesign}
      />

      <div className="flex flex-1 overflow-hidden">
        <aside className="flex w-[300px] shrink-0 flex-col gap-3 overflow-y-auto border-r p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
          <Group title="Zapata medianera (1)">
            <NumberRow label="Lx1 (cm)" value={request.section.Lx1} onChange={(v) => setSection({ Lx1: v })} step={5} />
            <NumberRow label="Ly1 (cm)" value={request.section.Ly1} onChange={(v) => setSection({ Ly1: v })} step={5} />
            <NumberRow label="Hf1 (cm)" value={request.section.Hf1} onChange={(v) => setSection({ Hf1: v })} step={5} />
            <NumberRow label="bc1 (cm)" value={request.section.bc1} onChange={(v) => setSection({ bc1: v })} step={5} />
            <NumberRow label="hc1 (cm)" value={request.section.hc1} onChange={(v) => setSection({ hc1: v })} step={5} />
            <NumberRow label="x_col1 — centro col al lindero (cm)" value={request.section.x_col1} onChange={(v) => setSection({ x_col1: v })} step={1} />
          </Group>

          <Group title="Zapata interior (2)">
            <NumberRow label="Lx2 (cm)" value={request.section.Lx2} onChange={(v) => setSection({ Lx2: v })} step={5} />
            <NumberRow label="Ly2 (cm)" value={request.section.Ly2} onChange={(v) => setSection({ Ly2: v })} step={5} />
            <NumberRow label="Hf2 (cm)" value={request.section.Hf2} onChange={(v) => setSection({ Hf2: v })} step={5} />
            <NumberRow label="bc2 (cm)" value={request.section.bc2} onChange={(v) => setSection({ bc2: v })} step={5} />
            <NumberRow label="hc2 (cm)" value={request.section.hc2} onChange={(v) => setSection({ hc2: v })} step={5} />
          </Group>

          <Group title="Viga de conexión">
            <NumberRow label="bb (cm)" value={request.section.bb} onChange={(v) => setSection({ bb: v })} step={5} />
            <NumberRow label="hb (cm)" value={request.section.hb} onChange={(v) => setSection({ hb: v })} step={5} />
            <NumberRow label="L_cols centro-a-centro (cm)" value={request.section.L_cols} onChange={(v) => setSection({ L_cols: v })} step={25} />
          </Group>

          <Group title="Materiales y suelo">
            <NumberRow label="f'c" value={request.materials.fc} onChange={(v) => setMaterials({ fc: v })} step={10} />
            <NumberRow label="fy" value={request.materials.fy} onChange={(v) => setMaterials({ fy: v })} step={100} />
            <NumberRow label="Recubrim. zapata" value={request.materials.cover} onChange={(v) => setMaterials({ cover: v })} step={0.5} />
            <NumberRow label="Recubrim. viga" value={request.materials.cover_beam} onChange={(v) => setMaterials({ cover_beam: v })} step={0.5} />
            <NumberRow label="σ_adm (kgf/cm²)" value={request.soil.sigma_adm} onChange={(v) => setSoil({ sigma_adm: v })} step={0.1} />
            <NumberRow label="df (cm)" value={request.section.df} onChange={(v) => setSection({ df: v })} step={10} />
          </Group>

          <Group title="Cargas (servicio + factor)">
            <NumberRow label="P1 servicio (kgf)" value={request.loads.P1} onChange={(v) => setLoads({ P1: v })} step={1000} />
            <NumberRow label="Pu1 factor (kgf)" value={request.loads.Pu1} onChange={(v) => setLoads({ Pu1: v })} step={1000} />
            <NumberRow label="P2 servicio (kgf)" value={request.loads.P2} onChange={(v) => setLoads({ P2: v })} step={1000} />
            <NumberRow label="Pu2 factor (kgf)" value={request.loads.Pu2} onChange={(v) => setLoads({ Pu2: v })} step={1000} />
          </Group>

          <Group title="Refuerzo zapata 1">
            <SelectRow label="Ø en X" value={request.reinforcement.diam_f1_x}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_f1_x: v as RebarDiam })} />
            <NumberRow label="N en X" value={request.reinforcement.n_f1_x}
              onChange={(v) => setReinforcement({ n_f1_x: Math.max(2, Math.round(v)) })} step={1} />
            <SelectRow label="Ø en Y" value={request.reinforcement.diam_f1_y}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_f1_y: v as RebarDiam })} />
            <NumberRow label="N en Y" value={request.reinforcement.n_f1_y}
              onChange={(v) => setReinforcement({ n_f1_y: Math.max(2, Math.round(v)) })} step={1} />
          </Group>

          <Group title="Refuerzo zapata 2">
            <SelectRow label="Ø en X" value={request.reinforcement.diam_f2_x}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_f2_x: v as RebarDiam })} />
            <NumberRow label="N en X" value={request.reinforcement.n_f2_x}
              onChange={(v) => setReinforcement({ n_f2_x: Math.max(2, Math.round(v)) })} step={1} />
            <SelectRow label="Ø en Y" value={request.reinforcement.diam_f2_y}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_f2_y: v as RebarDiam })} />
            <NumberRow label="N en Y" value={request.reinforcement.n_f2_y}
              onChange={(v) => setReinforcement({ n_f2_y: Math.max(2, Math.round(v)) })} step={1} />
          </Group>

          <Group title="Refuerzo viga conexión">
            <SelectRow label="Ø arriba" value={request.reinforcement.diam_beam_top}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_beam_top: v as RebarDiam })} />
            <NumberRow label="N arriba" value={request.reinforcement.n_beam_top}
              onChange={(v) => setReinforcement({ n_beam_top: Math.max(2, Math.round(v)) })} step={1} />
            <SelectRow label="Ø abajo" value={request.reinforcement.diam_beam_bot}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_beam_bot: v as RebarDiam })} />
            <NumberRow label="N abajo" value={request.reinforcement.n_beam_bot}
              onChange={(v) => setReinforcement({ n_beam_bot: Math.max(2, Math.round(v)) })} step={1} />
            <SelectRow label="Ø estribo" value={request.reinforcement.diam_stirrup}
              options={REBAR_DIAMS.slice(0, 3).map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_stirrup: v as RebarDiam })} />
            <NumberRow label="s estribo (cm)" value={request.reinforcement.s_stirrup}
              onChange={(v) => setReinforcement({ s_stirrup: v })} step={1} />
          </Group>
        </aside>

        <main className="flex-1 overflow-auto p-4" style={{ background: 'var(--surface-canvas)' }}>
          {data && (
            <div className="flex flex-col gap-4">
              <DiagramCard
                title={view === '3D' ? 'Vista 3D — sistema completo' : 'Esquema en planta + estática'}
                toolbar={
                  <div className="flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
                    {(['2D', '3D'] as const).map((m) => (
                      <button key={m} type="button" onClick={() => setView(m)}
                        className="h-7 px-3 text-[10px] font-semibold tracking-[0.12em]"
                        style={{ background: view === m ? 'var(--accent)' : 'transparent',
                          color: view === m ? 'var(--surface-base)' : 'var(--text-secondary)' }}>
                        {m}
                      </button>
                    ))}
                  </div>
                }
              >
                {view === '3D' ? (
                  <div style={{ width: 880, height: 500 }}>
                    <Connected3DView
                      Lx1={request.section.Lx1} Ly1={request.section.Ly1} Hf1={request.section.Hf1}
                      bc1={request.section.bc1} hc1={request.section.hc1} x_col1={request.section.x_col1}
                      Lx2={request.section.Lx2} Ly2={request.section.Ly2} Hf2={request.section.Hf2}
                      bc2={request.section.bc2} hc2={request.section.hc2}
                      bb={request.section.bb} hb={request.section.hb} L_cols={request.section.L_cols}
                      df={request.section.df} cover={request.materials.cover}
                    />
                  </div>
                ) : (
                  <ConnectedPlanDiagram
                    Lx1={request.section.Lx1} Ly1={request.section.Ly1}
                    bc1={request.section.bc1} hc1={request.section.hc1} x_col1={request.section.x_col1}
                    Lx2={request.section.Lx2} Ly2={request.section.Ly2}
                    bc2={request.section.bc2} hc2={request.section.hc2}
                    bb={request.section.bb} L_cols={request.section.L_cols}
                    e1={data.statics.e1} V_b={data.statics.V_b}
                    R1={data.statics.R1} R2={data.statics.R2} Pu1={request.loads.Pu1} Pu2={request.loads.Pu2}
                    M_beam={data.statics.M_beam}
                  />
                )}
              </DiagramCard>

              {view === '2D' && (
                <DiagramCard title="Elevación — corte longitudinal por las dos columnas y la viga de conexión">
                  <ConnectedElevationDiagram
                    Lx1={request.section.Lx1} Hf1={request.section.Hf1}
                    bc1={request.section.bc1} x_col1={request.section.x_col1}
                    Lx2={request.section.Lx2} Hf2={request.section.Hf2}
                    bc2={request.section.bc2}
                    bb={request.section.bb} hb={request.section.hb}
                    L_cols={request.section.L_cols} df={request.section.df}
                  />
                </DiagramCard>
              )}
            </div>
          )}
        </main>

        <aside className="flex w-[340px] shrink-0 flex-col gap-3 overflow-y-auto border-l p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
          {data && (
            <>
              <ResultCard title="Estática del sistema">
                <KV label="e₁ (excentr. col 1)" value={`${data.statics.e1.toFixed(1)} cm`} mono />
                <KV label="V_b cortante viga" value={`${(data.statics.V_b / 1000).toFixed(2)} tonf`} />
                <KV label="M_viga = Pu₁·e₁" value={`${(data.statics.M_beam / 1e5).toFixed(2)} tonf·m`} />
                <KV label="R₁ (a la zapata 1)" value={`${(data.statics.R1 / 1000).toFixed(2)} tonf (era ${(request.loads.Pu1 / 1000).toFixed(1)})`} />
                <KV label="R₂ (a la zapata 2)" value={`${(data.statics.R2 / 1000).toFixed(2)} tonf (era ${(request.loads.Pu2 / 1000).toFixed(1)})`} />
                <div className="mt-1 text-[9.5px]" style={{ color: 'var(--text-muted)', lineHeight: 1.35 }}>
                  La viga ALIVIA col 1 (R₁ &lt; Pu₁) y CARGA col 2 (R₂ &gt; Pu₂).
                </div>
              </ResultCard>

              {[data.footing1, data.footing2].map((f, idx) => {
                // Footing 1 uses section Lx1/Ly1/Hf1, footing 2 uses Lx2/Ly2/Hf2
                const isF1 = idx === 0;
                const Lx = isF1 ? request.section.Lx1 : request.section.Lx2;
                const Ly = isF1 ? request.section.Ly1 : request.section.Ly2;
                const Hf = isF1 ? request.section.Hf1 : request.section.Hf2;
                const rho_x = f.As_prov_x / (Ly * f.d_x);
                const rho_y = f.As_prov_y / (Lx * f.d_y);
                const rho_min = 0.0018 * Hf / Math.max(f.d_x, f.d_y);
                return (
                  <ResultCard key={f.label} title={`Zapata ${f.label}`}>
                    <KV label="Área" value={`${(f.A / 10000).toFixed(2)} m²`} />
                    <KV label="σ_max" value={`${f.sigma_max.toFixed(3)} kgf/cm² (lim ${f.sigma_adm_eff.toFixed(3)})`} mono />
                    <KV label="Cortante 1D" value={`${(f.Vu_oneway / 1000).toFixed(2)} / φVc ${(f.phi_Vc_oneway / 1000).toFixed(2)} tonf`} />
                    <KV label="Punzonado" value={`${(f.Vu_punch / 1000).toFixed(2)} / φVc ${(f.phi_Vc_punch / 1000).toFixed(2)} tonf`} />
                    <KV label="Mu X" value={`${(f.Mu_x / 1e5).toFixed(3)} tonf·m`} />
                    <KV label="Mu Y" value={`${(f.Mu_y / 1e5).toFixed(3)} tonf·m`} />
                    <KV label="Acero X" value={`${f.bars_label_x} (req ${f.As_req_x.toFixed(1)} prov ${f.As_prov_x.toFixed(1)})`} mono />
                    <KV label="Acero Y" value={`${f.bars_label_y} (req ${f.As_req_y.toFixed(1)} prov ${f.As_prov_y.toFixed(1)})`} mono />
                    <div className="mt-2 space-y-3">
                      <RhoBar
                        title="ρ X — As_x / (Ly · d_x)"
                        rho={rho_x}
                        rho_min={rho_min}
                        rho_max={0.04}
                        hideAdvice
                      />
                      <RhoBar
                        title="ρ Y — As_y / (Lx · d_y)"
                        rho={rho_y}
                        rho_min={rho_min}
                        rho_max={0.04}
                        hideAdvice
                      />
                    </div>
                    <div className="mt-2 text-[10px]" style={{ color: f.bearing_ok && f.oneway_ok && f.punch_ok && f.flex_ok ? 'var(--success)' : 'var(--danger)' }}>
                      {[f.bearing_ok && 'bearing', f.oneway_ok && '1D', f.punch_ok && 'punzon', f.flex_ok && 'flex'].filter(Boolean).join(', ')} OK
                      {!(f.bearing_ok && f.oneway_ok && f.punch_ok && f.flex_ok) && ' · revisar fallas'}
                    </div>
                  </ResultCard>
                );
              })}

              <ResultCard title="Viga de conexión">
                <KV label="Mu" value={`${(data.beam.Mu / 1e5).toFixed(2)} tonf·m`} />
                <KV label="Vu" value={`${(data.beam.Vu / 1000).toFixed(2)} tonf`} />
                <KV label="d efectivo" value={`${data.beam.d.toFixed(1)} cm`} />
                <KV label="As top req." value={`${data.beam.As_top_req.toFixed(2)} cm²`} />
                <KV label="As top prov." value={`${data.beam.As_top_provided.toFixed(2)} cm²`} />
                <KV label="As bot min" value={`${data.beam.As_bot_min.toFixed(2)} cm²`} />
                <KV label="As bot prov." value={`${data.beam.As_bot_provided.toFixed(2)} cm²`} />
                <KV label="Refuerzo top" value={data.beam.top_label} mono />
                <KV label="Refuerzo bot" value={data.beam.bot_label} mono />
                <KV label="φVc + φVs" value={`${((data.beam.phi_Vc + data.beam.phi_Vs) / 1000).toFixed(2)} tonf`} />
                <div className="mt-1 text-[10px]" style={{ color: data.beam.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.beam.ok ? '✓ OK' : '✗ REVISAR'}
                </div>
              </ResultCard>

              <ResultCard title="Verificaciones">
                {data.verifications.map((v) => (
                  <div key={v.id} className="flex items-start gap-2 py-0.5">
                    <span className="mt-0.5" style={{ color: v.ok ? 'var(--success)' : 'var(--danger)', fontWeight: 700, minWidth: 12 }}>
                      {v.ok ? '✓' : '✗'}
                    </span>
                    <span className="flex-1 text-[10px]" style={{ lineHeight: 1.35 }}>{v.label}</span>
                  </div>
                ))}
              </ResultCard>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function NumberRow({ label, value, onChange, step = 1 }: { label: string; value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <input type="number" value={value} step={step}
        onChange={(e) => { const v = parseFloat(e.target.value); if (!Number.isNaN(v)) onChange(v); }}
        className="w-[120px] rounded-[var(--radius-sm)] border px-2 py-1 text-right text-[11px] font-mono"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }} />
    </label>
  );
}
function SelectRow({ label, value, options, onChange }: { label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void }) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-[120px] rounded-[var(--radius-sm)] border px-2 py-1 text-[11px]"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
function ResultCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {children}
    </div>
  );
}
function KV({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-0.5 gap-2">
      <span className="text-[10px] shrink-0" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className={mono ? 'font-mono text-[10.5px] text-right' : 'text-[10.5px] text-right'} style={{ color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}
function DiagramCard({ title, children, toolbar }: { title: string; children: React.ReactNode; toolbar?: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-md)] border p-3" style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>{title}</div>
        {toolbar}
      </div>
      <div className="flex justify-center">{children}</div>
    </div>
  );
}

/* ====================================================================== */
/*  PLAN DIAGRAM + STATICS                                                  */
/* ====================================================================== */

function ConnectedPlanDiagram({
  Lx1, Ly1, bc1, hc1, x_col1,
  Lx2, Ly2, bc2, hc2,
  bb, L_cols,
  e1, V_b, R1, R2, Pu1, Pu2, M_beam,
}: {
  Lx1: number; Ly1: number; bc1: number; hc1: number; x_col1: number;
  Lx2: number; Ly2: number; bc2: number; hc2: number;
  bb: number; L_cols: number;
  e1: number; V_b: number; R1: number; R2: number; Pu1: number; Pu2: number; M_beam: number;
}) {
  const W = 880;
  const PAD = 30;
  const drawW = W - 2 * PAD;
  const xCol2 = x_col1 + L_cols;
  const totalSpan = xCol2 + Lx2 / 2;
  const maxLy = Math.max(Ly1, Ly2);
  const scaleX = drawW / totalSpan;
  const scaleY = Math.min(scaleX, 250 / maxLy);

  const X = (x: number) => PAD + x * scaleX;
  // Plan: y axis vertical, origin at top
  const yTop = 110;
  const Yc = (y: number) => yTop + (maxLy / 2 - y) * scaleY;   // y_cm centered on 0
  const H = yTop + maxLy * scaleY + 100;

  // Footing 1 polygon
  const f1_x1 = 0, f1_x2 = Lx1;
  const f1_y1 = -Ly1 / 2, f1_y2 = Ly1 / 2;
  // Footing 2
  const f2_x1 = xCol2 - Lx2 / 2, f2_x2 = xCol2 + Lx2 / 2;
  const f2_y1 = -Ly2 / 2, f2_y2 = Ly2 / 2;
  // Connection beam — between column faces, centered in y
  const beam_x1 = x_col1 + bc1 / 2;
  const beam_x2 = xCol2 - bc2 / 2;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <defs>
        <pattern id="hatch-conn" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.20" strokeWidth="1" />
        </pattern>
      </defs>

      {/* Property line */}
      <line x1={X(0)} y1={20} x2={X(0)} y2={H - 60} stroke="var(--danger)" strokeWidth="1.4" strokeDasharray="6 4" />
      <text x={X(0) - 4} y={32} textAnchor="end" fontSize="9" fontFamily="monospace" fill="var(--danger)" style={{ fontWeight: 700 }}>
        Lindero
      </text>

      {/* Footing 1 */}
      <rect x={X(f1_x1)} y={Yc(f1_y2)} width={(f1_x2 - f1_x1) * scaleX} height={(f1_y2 - f1_y1) * scaleY}
        fill="url(#hatch-conn)" stroke="var(--concrete-outline)" strokeWidth="1.3" />
      {/* Footing 2 */}
      <rect x={X(f2_x1)} y={Yc(f2_y2)} width={(f2_x2 - f2_x1) * scaleX} height={(f2_y2 - f2_y1) * scaleY}
        fill="url(#hatch-conn)" stroke="var(--concrete-outline)" strokeWidth="1.3" />

      {/* Connection beam */}
      <rect x={X(beam_x1)} y={Yc(bb / 2)} width={(beam_x2 - beam_x1) * scaleX} height={bb * scaleY}
        fill="rgba(245,158,11,0.20)" stroke="var(--accent)" strokeWidth="1.3" />
      <text x={X((beam_x1 + beam_x2) / 2)} y={Yc(0) + 4} textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill="var(--accent)" style={{ fontWeight: 700 }}>VIGA CONEXIÓN</text>

      {/* Column 1 */}
      <rect x={X(x_col1) - bc1 * scaleX / 2} y={Yc(hc1 / 2)}
        width={bc1 * scaleX} height={hc1 * scaleY}
        fill="rgba(220,38,38,0.25)" stroke="var(--danger)" strokeWidth="1.4" />
      <text x={X(x_col1)} y={Yc(-hc1 / 2 - 6)} textAnchor="middle" fontSize="9.5" fontFamily="monospace"
        fill="var(--danger)" style={{ fontWeight: 700 }}>Col 1 (medianera)</text>

      {/* Column 2 */}
      <rect x={X(xCol2) - bc2 * scaleX / 2} y={Yc(hc2 / 2)}
        width={bc2 * scaleX} height={hc2 * scaleY}
        fill="rgba(34,197,94,0.25)" stroke="var(--success)" strokeWidth="1.4" />
      <text x={X(xCol2)} y={Yc(-hc2 / 2 - 6)} textAnchor="middle" fontSize="9.5" fontFamily="monospace"
        fill="var(--success)" style={{ fontWeight: 700 }}>Col 2 (interior)</text>

      {/* Centroid of footing 1 (dashed) */}
      <line x1={X(Lx1 / 2)} y1={Yc(f1_y2) - 4} x2={X(Lx1 / 2)} y2={Yc(f1_y1) + 4}
        stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="2 2" />
      <text x={X(Lx1 / 2)} y={Yc(f1_y2) - 8} textAnchor="middle" fontSize="8" fontFamily="monospace" fill="var(--text-muted)">
        CG f1
      </text>

      {/* Eccentricity arrow */}
      <line x1={X(x_col1)} y1={Yc(f1_y1) + 18} x2={X(Lx1 / 2)} y2={Yc(f1_y1) + 18}
        stroke="var(--brand-violet, #8B5CF6)" strokeWidth="1.4" />
      <text x={X((x_col1 + Lx1 / 2) / 2)} y={Yc(f1_y1) + 28} textAnchor="middle" fontSize="9.5" fontFamily="monospace"
        fill="var(--brand-violet, #8B5CF6)" style={{ fontWeight: 700 }}>
        e₁ = {e1.toFixed(1)} cm
      </text>

      {/* Forces table */}
      <g transform={`translate(${PAD}, ${H - 56})`}>
        <text x="0" y="0" fontSize="10" fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 700 }}>
          Estática (Pu₁·e₁ se transfiere por la viga a la col 2):
        </text>
        <text x="0" y="14" fontSize="9.5" fontFamily="monospace" fill="var(--text-secondary)">
          V_b = Pu₁·e₁ / L_cols = {(V_b / 1000).toFixed(2)} tonf  ·  M_viga = {(M_beam / 1e5).toFixed(2)} tonf·m
        </text>
        <text x="0" y="28" fontSize="9.5" fontFamily="monospace" fill="var(--text-secondary)">
          R₁ = Pu₁ − V_b = {(R1 / 1000).toFixed(2)} tonf  (era Pu₁ = {(Pu1 / 1000).toFixed(1)} tonf)
        </text>
        <text x="0" y="42" fontSize="9.5" fontFamily="monospace" fill="var(--text-secondary)">
          R₂ = Pu₂ + V_b = {(R2 / 1000).toFixed(2)} tonf  (era Pu₂ = {(Pu2 / 1000).toFixed(1)} tonf)
        </text>
      </g>
    </svg>
  );
}

/* ====================================================================== */
/*  ELEVATION — connected footing system                                    */
/* ====================================================================== */

function ConnectedElevationDiagram({
  Lx1, Hf1, bc1, x_col1,
  Lx2, Hf2, bc2,
  bb, hb, L_cols, df,
}: {
  Lx1: number; Hf1: number; bc1: number; x_col1: number;
  Lx2: number; Hf2: number; bc2: number;
  bb: number; hb: number; L_cols: number; df: number;
}) {
  // Coordinate system: property line at x = 0
  const xCol1 = x_col1;
  const xCol2 = x_col1 + L_cols;
  const totalSpan = xCol2 + Lx2 / 2;     // right edge of footing 2

  const W = 880;
  const PAD = 50;
  const drawW = W - 2 * PAD;
  const scaleX = drawW / totalSpan;
  const scaleY = Math.min(scaleX, 100 / Math.max(Math.max(Hf1, Hf2), 1));
  const xL = PAD;
  const X = (x_cm: number) => xL + x_cm * scaleX;

  const yGrade = 70;
  const ySlab1Top = yGrade + (df - Hf1) * scaleY;
  const ySlab1Bot = ySlab1Top + Hf1 * scaleY;
  const ySlab2Top = yGrade + (df - Hf2) * scaleY;
  const ySlab2Bot = ySlab2Top + Hf2 * scaleY;
  const H = Math.max(ySlab1Bot, ySlab2Bot) + 60;

  const colTop = yGrade - 70;

  // Footings
  const f1_x1 = 0, f1_x2 = Lx1;
  const f2_x1 = xCol2 - Lx2 / 2, f2_x2 = xCol2 + Lx2 / 2;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <defs>
        <pattern id="hatch-conn-elev" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.18" strokeWidth="1" />
        </pattern>
        <pattern id="soil-conn-elev" patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill="#7C2D12" fillOpacity="0.10" />
          <circle cx="2" cy="2" r="0.6" fill="#7C2D12" fillOpacity="0.4" />
          <circle cx="6" cy="6" r="0.6" fill="#7C2D12" fillOpacity="0.4" />
        </pattern>
      </defs>

      {/* Property line */}
      <line x1={X(0)} y1={20} x2={X(0)} y2={H - 20}
        stroke="var(--danger)" strokeWidth="1.4" strokeDasharray="6 4" />
      <text x={X(0) - 4} y={32} textAnchor="end" fontSize="9"
        fontFamily="monospace" fill="var(--danger)" style={{ fontWeight: 700 }}>
        Lindero
      </text>

      {/* Soil */}
      <rect x={xL - 10} y={yGrade} width={totalSpan * scaleX + 60} height={Math.max(ySlab1Bot, ySlab2Bot) - yGrade + 10}
        fill="url(#soil-conn-elev)" />
      <line x1={xL - 10} y1={yGrade} x2={X(totalSpan) + 30} y2={yGrade}
        stroke="#7C2D12" strokeWidth="1.4" />
      <text x={X(totalSpan) + 8} y={yGrade - 4} fontSize="10" fontFamily="monospace" fill="#7C2D12">NPT</text>

      {/* Footing 1 (medianera) */}
      <rect x={X(f1_x1)} y={ySlab1Top} width={(f1_x2 - f1_x1) * scaleX} height={Hf1 * scaleY}
        fill="url(#hatch-conn-elev)" stroke="var(--concrete-outline)" strokeWidth="1.4" />
      <text x={X((f1_x1 + f1_x2) / 2)} y={ySlab1Bot + 12} textAnchor="middle"
        fontSize="9.5" fontFamily="monospace" fill="var(--text-muted)">
        Zapata 1 · Lx1={Lx1} · Hf1={Hf1}
      </text>

      {/* Footing 2 (interior) */}
      <rect x={X(f2_x1)} y={ySlab2Top} width={(f2_x2 - f2_x1) * scaleX} height={Hf2 * scaleY}
        fill="url(#hatch-conn-elev)" stroke="var(--concrete-outline)" strokeWidth="1.4" />
      <text x={X((f2_x1 + f2_x2) / 2)} y={ySlab2Bot + 12} textAnchor="middle"
        fontSize="9.5" fontFamily="monospace" fill="var(--text-muted)">
        Zapata 2 · Lx2={Lx2} · Hf2={Hf2}
      </text>

      {/* Column 1 (medianera) */}
      <rect x={X(xCol1) - (bc1 * scaleX) / 2} y={colTop}
        width={bc1 * scaleX} height={ySlab1Top - colTop}
        fill="rgba(220,38,38,0.20)" stroke="var(--danger)" strokeWidth="1.3" />
      <text x={X(xCol1)} y={colTop - 4} textAnchor="middle" fontSize="10"
        fontFamily="monospace" fill="var(--danger)" style={{ fontWeight: 700 }}>
        Col 1 (mediañera)
      </text>

      {/* Column 2 (interior) */}
      <rect x={X(xCol2) - (bc2 * scaleX) / 2} y={colTop}
        width={bc2 * scaleX} height={ySlab2Top - colTop}
        fill="rgba(34,197,94,0.20)" stroke="var(--success)" strokeWidth="1.3" />
      <text x={X(xCol2)} y={colTop - 4} textAnchor="middle" fontSize="10"
        fontFamily="monospace" fill="var(--success)" style={{ fontWeight: 700 }}>
        Col 2 (interior)
      </text>

      {/* Connection beam between columns — sitting at grade level, hangs down */}
      {(() => {
        const beamX1 = xCol1 + bc1 / 2;
        const beamX2 = xCol2 - bc2 / 2;
        const beamTopY = yGrade;
        const beamH = hb * scaleY;
        const beamBotY = beamTopY + beamH;
        return (
          <g>
            <rect x={X(beamX1)} y={beamTopY}
              width={(beamX2 - beamX1) * scaleX} height={beamH}
              fill="rgba(245,158,11,0.30)" stroke="var(--accent)" strokeWidth="1.4" />
            <text x={X((beamX1 + beamX2) / 2)} y={(beamTopY + beamBotY) / 2 + 3}
              textAnchor="middle" fontSize="10" fontFamily="monospace"
              fill="var(--accent)" style={{ fontWeight: 700 }}>
              VIGA CONEXIÓN ({bb} × {hb} cm)
            </text>
          </g>
        );
      })()}

      {/* Dimensions */}
      <text x={X(totalSpan / 2)} y={H - 6} textAnchor="middle" fontSize="11"
        fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        L_cols = {L_cols} cm  ·  df = {df} cm
      </text>
    </svg>
  );
}
